// Trusted customer reviews section
//
// The review slider under the product. Everything around it is rendered in
// sections/trusted-reviews.liquid from the product's review metafields; this builds a slide per
// review and hands them to Swiper.
//
// No request of its own. The reviews slideout in the product info column already loads them on
// page load — it has to, because they feed the product's structured data — so this reads them off
// that element, either from the "product-reviews:loaded" event or straight off the property if it
// has already fired by the time this connects. The panel lives inside <product-rerender>, so a
// variant change replaces it and it loads again; the event fires from the new one and the slides
// are rebuilt, which is why the listener stays on rather than running once.

import Swiper from 'swiper';
import { A11y, Navigation } from 'swiper/modules';
import { formatDate } from './_product-reviews.js';

const STAR_COUNT = 5;

// Enough to be worth paging through without the slider becoming a substitute for the panel, which
// is what the All reviews button is for
const MAX_REVIEWS = 10;

// Newest first, as the section leads on the date. The panel loads its first page sorted Yotpo's
// way — reviews with photos first — so this sorts what it has rather than trusting the order.
//
// Which also sets the ceiling: on a product with more reviews than the panel's page size, this is
// the newest of that first page rather than the newest overall. Nothing short of a second request
// can do better, and the section is a taste of the reviews rather than the whole of them
function newestFirst(reviews) {
	return reviews
		.filter((review) => review && review.content)
		.slice()
		.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
		.slice(0, MAX_REVIEWS);
}

class TrustedReviews extends HTMLElement {
	connectedCallback() {
		if (this.hasAttribute('ready')) {
			return;
		}

		this.setAttribute('ready', '');

		this.card = this.querySelector('[data-card]');
		this.slider = this.querySelector('[data-slider]');
		this.slides = this.querySelector('[data-slides]');
		this.slideTemplate = this.querySelector('template[data-slide]');
		this.nav = this.querySelector('[data-nav]');

		this.querySelector('[data-write]')?.addEventListener('click', () => this.openWriteForm());

		document.addEventListener('product-reviews:loaded', (event) => {
			if (event.detail.productId === this.getAttribute('product-id')) {
				this.setReviews(event.detail.reviews);
			}
		});

		// Already loaded: this section is below the product, so the panel has usually been through
		// its first fetch before this element is upgraded
		if (this.panel?.structuredDataReviews) {
			this.setReviews(this.panel.structuredDataReviews);
		}
	}

	disconnectedCallback() {
		this.swiper?.destroy();
		this.swiper = null;
	}

	// The id belongs to the drawer the panel sits in — it is what the buttons' aria-controls opens —
	// so the element holding the reviews is a step inside it
	get drawer() {
		return document.getElementById(this.getAttribute('panel'));
	}

	get panel() {
		return this.drawer?.querySelector('product-reviews') || null;
	}

	setReviews(reviews) {
		const ordered = newestFirst(reviews || []);

		// The section only renders on a product whose metafields say it has reviews, and those are
		// synced rather than live, so this is what a product looks like between its first review
		// being left and Yotpo passing it on
		if (ordered.length === 0) {
			this.closest('.shopify-section')?.setAttribute('hidden', '');

			return;
		}

		this.slides.replaceChildren(...ordered.map((review) => this.buildSlide(review)));
		this.nav.hidden = ordered.length < 2;

		// Before Swiper, not after: it measures the slides as it starts, and a card that is still
		// hidden measures nothing at all
		this.card.hidden = false;

		if (this.swiper) {
			this.swiper.update();
			this.swiper.slideTo(0, 0);

			return;
		}

		this.swiper = new Swiper(this.slider, {
			modules: [Navigation, A11y],
			slidesPerView: 1,

			// Only ever seen mid-move, where it keeps two reviews from running into each other
			spaceBetween: 40,

			// Reviews run from a line to a paragraph, so the card takes the height of the one being
			// read rather than the longest of them
			autoHeight: true,

			// Wraps at both ends without cloning slides the way loop does, so neither control is
			// ever a dead end and there is still exactly one copy of each review on the page
			rewind: true,
			navigation: {
				prevEl: this.nav.querySelector('[data-previous]'),
				nextEl: this.nav.querySelector('[data-next]'),
			},

			// Swiper labels the controls itself, and the default wording talks about slides. These
			// keep what it says to a screen reader matching what is written on them
			a11y: {
				prevSlideMessage: 'Previous review',
				nextSlideMessage: 'Next review',
			},
		});
	}

	buildSlide(review) {
		const slide = this.slideTemplate.content.cloneNode(true).firstElementChild;
		const score = Number(review.score) || 0;
		const stars = slide.querySelector('[data-stars]');
		const date = slide.querySelector('[data-date]');

		stars.replaceChildren(this.starsFor(score));
		stars.setAttribute('aria-label', `${score} out of ${STAR_COUNT} stars`);

		date.textContent = formatDate(review.created_at);
		date.setAttribute('datetime', review.created_at || '');

		// textContent throughout: the review is somebody else's words, and none of it is markup
		slide.querySelector('[data-content]').textContent = review.content;
		slide.querySelector('[data-author]').textContent = this.authorFor(review);

		return slide;
	}

	authorFor(review) {
		const name = review.user?.display_name || 'Anonymous';

		return review.verified_buyer ? `${name} (Verified Buyer)` : name;
	}

	// Same half star thresholds as product-rating.liquid and the panel, so a 4.4 reads the same
	// everywhere on the page
	starsFor(score) {
		const decimal = score % 1;
		const fragment = document.createDocumentFragment();

		let partial = 0;

		if (decimal >= 0.3 && decimal <= 0.7) {
			partial = 0.5;
		} else if (decimal > 0.7) {
			partial = 1;
		}

		for (let index = 1; index <= STAR_COUNT; index += 1) {
			let type = 'empty';

			if (index <= score) {
				type = 'full';
			} else if (partial === 0.5) {
				type = 'half';
				partial = 0;
			} else if (partial === 1) {
				type = 'full';
				partial = 0;
			}

			fragment.appendChild(this.starTemplate(type));
		}

		return fragment;
	}

	starTemplate(type) {
		const template = this.querySelector(`template[data-star="${type}"]`);

		return template ? template.content.cloneNode(true) : document.createDocumentFragment();
	}

	// The panel's own Write a review button lives inside the drawer, so from out here the way in is
	// to let the drawer open on its aria-controls and switch modes once it has. Waiting for
	// after-show matters: the drawer moves focus itself on the way in, and a mode set before that
	// lands its own focus first and then has it taken away
	openWriteForm() {
		const drawer = this.drawer;
		const panel = this.panel;

		if (!panel || !drawer) {
			return;
		}

		drawer.addEventListener('dialog:after-show', () => panel.setMode('form'), { once: true });
	}
}

if (!customElements.get('trusted-reviews')) {
	customElements.define('trusted-reviews', TrustedReviews);
}
